// src/services/authService.js
import axios from "axios";

const API_BASE =
  (typeof window !== "undefined" && window.__APP_CONFIG__?.API_BASE) ||
  (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE) || "";

const api = axios.create({ baseURL: API_BASE, withCredentials: false });

// =============================
// ストレージ共通ユーティリティ
// rememberMe=true  → localStorage（ブラウザを閉じても保持）
// rememberMe=false → sessionStorage（タブ/ブラウザを閉じると消去）
// =============================
function saveToken(key, value, persist) {
  if (persist) {
    localStorage.setItem(key, value);
    sessionStorage.removeItem(key);
  } else {
    sessionStorage.setItem(key, value);
    localStorage.removeItem(key);
  }
}

function getToken(key) {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key) ?? null;
}

// =============================
// ログイン画面の自動入力（社員番号＋パスワード / 社員ログインのみ）
// base64 は暗号化ではなく、DevTools などで偶然目に入るのを防ぐだけの難読化。
// 端末を他人と共有する場合はチェックを外して使う前提。
// =============================
const SAVED_LOGIN_KEY = "savedLogin:employee";
const SAVED_ID_KEY = "savedEmployeeId"; // 社員番号のみ保存していた版のキー（引き継ぎ用）

export function getSavedLogin() {
  try {
    const raw = localStorage.getItem(SAVED_LOGIN_KEY);
    if (raw) {
      const saved = JSON.parse(decodeURIComponent(atob(raw)));
      if (typeof saved?.username === "string") {
        return { username: saved.username, password: typeof saved.password === "string" ? saved.password : "" };
      }
    }
    // 社員番号だけ保存されている場合はそれを引き継ぐ
    const id = localStorage.getItem(SAVED_ID_KEY);
    if (id) return { username: id, password: "" };
    return null;
  } catch (_) {
    try { localStorage.removeItem(SAVED_LOGIN_KEY); } catch (_) {}
    return null;
  }
}

export function saveLogin(username, password = "") {
  // プライベートモード等で localStorage が使えなくてもログインは続行させる
  try {
    localStorage.setItem(
      SAVED_LOGIN_KEY,
      btoa(encodeURIComponent(JSON.stringify({ username, password })))
    );
    localStorage.removeItem(SAVED_ID_KEY);
  } catch (_) {}
}

export function clearSavedLogin() {
  try {
    localStorage.removeItem(SAVED_LOGIN_KEY);
    localStorage.removeItem(SAVED_ID_KEY);
  } catch (_) {}
}

api.interceptors.request.use((config) => {
  const token = getToken("access");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// =============================
// 社員ログイン
// =============================
export async function employeeLogin(username, password, rememberMe = false) {
  const { data } = await api.post("/api/auth/token/", { username, password });
  saveToken("access", data.access, rememberMe);
  saveToken("refresh", data.refresh, rememberMe);
  return await me(data.access);
}

// =============================
// 管理者ログイン
// =============================
export async function adminLogin(username, password, rememberMe = false) {
  // 1) トークン発行
  const { data: tokenData } = await api.post("/api/admin/token/", { username, password, employee_id: username });
  const { access, refresh } = tokenData;
  saveToken("access", access, rememberMe);
  saveToken("refresh", refresh, rememberMe);


  // 2) 管理者情報取得
  const { data: me } = await api.get("/api/admin/me/");

  // 3) 権限チェック
  /*
  if (!(me?.is_staff || me?.is_superuser)) {
    clearAuth();
    throw new Error("NOT_ADMIN");
  }

  localStorage.setItem("adminUser", JSON.stringify(me));
  return me;
  */
  const isStaff = me?.is_staff === true;
  const isSuper = me?.is_superuser === true;
  const perms = Array.isArray(me?.permissions) ? me.permissions : [];
  const hasAdminAccess = isStaff || isSuper || perms.length > 0;
  
  if (!hasAdminAccess) {
    clearAuth(); 
    throw new Error("管理者権限、または該当機能のアクセス権限がありません。");
  }
  localStorage.setItem("adminUser", JSON.stringify(me));
  return {
    ...me,
    ...tokenData, 
    user: tokenData.user || me
  };

}

// =============================
// リフレッシュ処理
// =============================
export async function refresh() {
  const token = getToken("refresh");
  if (!token) throw new Error("no refresh");
  const { data } = await api.post("/api/auth/token/refresh/", { refresh: token });
  // 元と同じストレージに保存
  const persist = !!localStorage.getItem("refresh");
  saveToken("access", data.access, persist);
  // ROTATE_REFRESH_TOKENS=True のとき新しいリフレッシュトークンが返る。
  // これを保存しないと有効期限が延長されず、初回ログインから一定期間で再ログインになる。
  if (data.refresh) saveToken("refresh", data.refresh, persist);
  return data.access;
}

// =============================
// 社員用 me()
// =============================
export async function me(freshToken = null) {
  try {
    const url = `/api/employees/me/?_=${Date.now()}`;
    const config = {
      headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
    };
    if (freshToken) {
      config.headers.Authorization = `Bearer ${freshToken}`;
    }
    const { data } = await api.get(url, config);
    localStorage.setItem("user", JSON.stringify(data));
    return data;
  } catch (e) {
    try {
      await refresh();
      return me();
    } catch {
      logout();
      throw e;
    }
  }
}

// =============================
// 共通ユーティリティ
// =============================
export function logout() {
  ["access", "refresh", "user", "adminUser"].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  if (typeof window !== "undefined" && "caches" in window) {
    caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
  }
}

export function clearAuth() {
  ["access", "refresh"].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

export function setUser(user) {
  localStorage.setItem("user", JSON.stringify(user));
}

export function getUser() {
  const raw = localStorage.getItem("user");
  return raw ? JSON.parse(raw) : null;
}

export function getAccessToken() {
  return getToken("access");
}

export function getRefreshToken() {
  return getToken("refresh");
}

export async function refreshAccessToken() {
  const token = getToken("refresh");
  if (!token) throw new Error("REFRESH_FAILED");
  const res = await fetch("/api/auth/token/refresh/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh: token }),
  });
  if (!res.ok) throw new Error("REFRESH_FAILED");
  const data = await res.json();
  const persist = !!localStorage.getItem("refresh");
  saveToken("access", data.access, persist);
  // ローテーションで新しく発行されたリフレッシュトークンを保存（有効期限の延長）
  if (data.refresh) saveToken("refresh", data.refresh, persist);
  return data.access;
}
